// Centralized configuration management
export interface AppConfig {
  // API Configuration
  api: {
    baseUrl: string;
    timeout: number;
    retryAttempts: number;
  };
  
  // Stripe Configuration
  stripe: {
    publishableKey: string;
  };
  
  // Environment
  environment: 'development' | 'staging' | 'production';
  
  // Feature Flags
  features: {
    enableAnalytics: boolean;
    enableDebugMode: boolean;
    enablePerformanceMonitoring: boolean;
  };
  
  // Performance Configuration
  performance: {
    enableCodeSplitting: boolean;
    enableLazyLoading: boolean;
    enableMemoization: boolean;
    bundleAnalyzerEnabled: boolean;
  };
}

// Environment variable validation
// During build/prerender, allow empty values with defaults to prevent build failures
const validateConfig = (config: Partial<AppConfig>): AppConfig => {
  // Check if we're in a browser context (window exists) vs Node.js build context
  const isBrowser = typeof window !== 'undefined';
  const isBuildTime = !isBrowser && (process.env.NODE_ENV === 'production' || !process.env.REACT_APP_BASE_URL);
  
  // Provide defaults during build time if values are missing
  // In browser, use actual values or empty strings (will be validated at runtime when needed)
  const apiBaseUrl = config.api?.baseUrl || (isBuildTime ? 'http://localhost:8000/api' : '') || '';
  const stripeKey = config.stripe?.publishableKey || (isBuildTime ? 'pk_test_build_time_placeholder' : '') || '';
  
  // Only validate in browser at runtime, not during build/prerender
  if (isBrowser && !isBuildTime) {
    const requiredFields = [
      { path: 'api.baseUrl', value: apiBaseUrl },
      { path: 'stripe.publishableKey', value: stripeKey }
    ];
    
    for (const field of requiredFields) {
      if (!field.value) {
        console.warn(`Missing required configuration: ${field.path} - using default`);
      }
    }
  }
  
  return {
    ...config,
    api: {
      ...config.api,
      baseUrl: apiBaseUrl,
      timeout: config.api?.timeout || 30000,
      retryAttempts: config.api?.retryAttempts || 3,
    },
    stripe: {
      publishableKey: stripeKey,
    },
    environment: config.environment || 'production',
    features: config.features || {
      enableAnalytics: false,
      enableDebugMode: false,
      enablePerformanceMonitoring: false,
    },
    performance: config.performance || {
      enableCodeSplitting: true,
      enableLazyLoading: true,
      enableMemoization: true,
      bundleAnalyzerEnabled: false,
    },
  } as AppConfig;
};

// Get environment-specific configuration
const getEnvironmentConfig = (): Partial<AppConfig> => {
  const env = process.env.NODE_ENV || 'development';
  
  const baseConfig: Partial<AppConfig> = {
    api: {
      baseUrl: process.env.REACT_APP_BASE_URL || '',
      timeout: 30000,
      retryAttempts: 3,
    },
    stripe: {
      publishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '',
    },
    environment: env as 'development' | 'staging' | 'production',
    features: {
      enableAnalytics: env === 'production',
      enableDebugMode: env === 'development',
      enablePerformanceMonitoring: env === 'production',
    },
    performance: {
      enableCodeSplitting: true,
      enableLazyLoading: true,
      enableMemoization: true,
      bundleAnalyzerEnabled: env === 'development',
    },
  };
  
  return baseConfig;
};

// Export validated configuration
export const config: AppConfig = validateConfig(getEnvironmentConfig());

// Configuration utilities
export const isDevelopment = config.environment === 'development';
export const isProduction = config.environment === 'production';
export const isStaging = config.environment === 'staging';

// Performance utilities
export const shouldEnableAnalytics = config.features.enableAnalytics;
export const shouldEnableDebugMode = config.features.enableDebugMode;
export const shouldEnablePerformanceMonitoring = config.features.enablePerformanceMonitoring;

// API utilities
export const getApiConfig = () => config.api;
export const getStripeConfig = () => config.stripe; 